import React from 'react';
import { ChevronRight, ChevronLeft, Check, Mail, Calendar, X } from 'lucide-react';
import { useAwningPricing } from '../hooks/useAwningPricing';
import { useAwningModels } from '../hooks/useAwningModels';
import { supabase } from '../config/supabase';
import { calculateDeliveryFee } from '../utils/delivery';

// ===== Types =====
interface AwningFormData {
  model: string;
  size: string;
  color: string;
  location: string;
  addOns: string[];
  name: string;
  email: string;
  phone: string;
  streetAddress: string;
  city: string;
  province: string;
  postalCode: string;
}

interface FinancingSubmissionData {
  name: string;
  email: string;
  phone: string;
  awning_size: string;
  total_amount: number;
  required_deposit: number;
  financing_amount: number;
  source: string;
  referrer: string;
  user_agent: string;
  submissionId?: string | number;
}

// ===== Constants =====
const AWNING_COLORS = [
  { id: 'charcoal', name: 'Charcoal', color: '#36454F' },
  { id: 'white', name: 'White', color: '#FFFFFF' },
  { id: 'beige', name: 'Beige', color: '#F5F5DC' },
  { id: 'navy', name: 'Navy', color: '#000080' },
  { id: 'forest-green', name: 'Forest Green', color: '#228B22' },
  { id: 'burgundy', name: 'Burgundy', color: '#800020' },
  { id: 'blue-white-stripe', name: 'Blue & White Stripe', color: 'linear-gradient(90deg, #4169E1 50%, #FFFFFF 50%)' },
  { id: 'other', name: 'Other Color', color: '#E5E7EB' }
];

const AWNING_WALL_TYPES = [
  { id: 'brick', name: 'Brick', extraCost: 0 },
  { id: 'vinyl-siding', name: 'Vinyl Siding', extraCost: 0 },
  { id: 'wood', name: 'Wood', extraCost: 0 },
  { id: 'composite-siding', name: 'Composite Siding', extraCost: 0 },
  { id: 'stone', name: 'Stone', extraCost: 250 },
  { id: 'other', name: 'Other', extraCost: 0 }
];

// Note: AWNING_ADDONS is now loaded dynamically from the database via useAwningModels hook
// This ensures compatibility filtering works correctly with the new Evie model system

// Validation patterns
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^[+]?[1]?[\s\-.]?[(]?[0-9]{3}[)]?[\s\-.]?[0-9]{3}[\s\-.]?[0-9]{4}$/;
const CA_POSTAL = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

// Utility functions
const currency = (amount: number) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);

// Financing calculations
const calculateMonthlyPayment = (principal: number, annualRate: number, months: number) => {
  if (annualRate === 0) {
    return principal / months;
  }
  const monthlyRate = annualRate / 12 / 100;
  return (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
};

const normalizePostal = (postal: string) => {
  return postal.replace(/\s+/g, ' ').toUpperCase().trim();
};

export const AwningQuoteForm: React.FC = () => {
  const { pricingByModel, isLoading: pricingLoading, error: pricingError } = useAwningPricing();
  const { modelCards, addonCards, isLoading: modelsLoading, getCompatibleAddons } = useAwningModels();

  const [step, setStep] = React.useState(1);
  const [formData, setFormData] = React.useState<AwningFormData>({
    model: '',
    size: '',
    color: '',
    location: '',
    addOns: [],
    name: '',
    email: '',
    phone: '',
    streetAddress: '',
    city: '',
    province: '',
    postalCode: ''
  });

  const [msg, setMsg] = React.useState('');
  const [emailSending, setEmailSending] = React.useState(false);
  const [emailSent, setEmailSent] = React.useState(false);
  const [showExpensiveOptions, setShowExpensiveOptions] = React.useState(false);
  // const [quickPhoneOpen, setQuickPhoneOpen] = React.useState(false);
  // const [quickCallbackSubmitting, setQuickCallbackSubmitting] = React.useState(false);
  const [customColor, setCustomColor] = React.useState('');

  // Financing states
  const [financingModalOpen, setFinancingModalOpen] = React.useState(false);
  const [financingContactInfo, setFinancingContactInfo] = React.useState({ name: '', email: '', phone: '' });
  const [financingSuccessOpen, setFinancingSuccessOpen] = React.useState(false);
  const [submittedFinancingData, setSubmittedFinancingData] = React.useState<FinancingSubmissionData | null>(null);
  const [contactSubmitSuccessOpen, setContactSubmitSuccessOpen] = React.useState(false);

  // Get current pricing based on selected model and size
  const currentModelPricing = formData.model ? pricingByModel[formData.model] || [] : [];
  const currentPricing = currentModelPricing.find(p => p.size_display_ft === formData.size);
  const basePrice = currentPricing?.retail_price || 0;
  const installCost = currentPricing?.install_cost || 0;

  // Get compatible addons for the selected model
  const compatibleAddons = formData.model ? getCompatibleAddons(formData.model) : addonCards;

  // Calculate add-ons total using the new addon structure
  const addOnsTotal = formData.addOns.reduce((total, addonId) => {
    const addon = compatibleAddons.find(a => a.id === addonId);
    return total + (addon?.price || 0);
  }, 0);

  // Calculate wall type surcharge
  const wallType = AWNING_WALL_TYPES.find(w => w.id === formData.location);
  const wallTypeSurcharge = wallType?.extraCost || 0;

  // Delivery fee: tiered by distance from GTA (see utils/delivery.ts)
  const deliveryFee = calculateDeliveryFee(formData.city, formData.province);

  const subtotal = basePrice + addOnsTotal + installCost + wallTypeSurcharge + deliveryFee;
  const hst = subtotal * 0.13;
  const totalWithHST = subtotal + hst;
  const totalMax = totalWithHST;

  // Financing calculations
  const costPrice = currentPricing?.cost_price || basePrice * 0.6; // Fallback if cost_price not available
  const requiredDeposit = Math.ceil(costPrice / 1000) * 1000; // Round up to next 1000
  const financingAmount = Math.max(0, totalMax - requiredDeposit);

  // Financing options
  const financingOptions = React.useMemo(() => [
    {
      term: 12,
      rate: 0,
      label: '12 months',
      description: '0% Interest',
      payment: calculateMonthlyPayment(financingAmount, 0, 12)
    },
    {
      term: 24,
      rate: 9,
      label: '24 months',
      description: '9% APR',
      payment: calculateMonthlyPayment(financingAmount, 9, 24)
    },
    {
      term: 36,
      rate: 9,
      label: '36 months',
      description: '9% APR',
      payment: calculateMonthlyPayment(financingAmount, 9, 36)
    }
  ], [financingAmount]);

  // Financing submission handler
  const handleFinancingSubmit = async () => {
    if (!financingContactInfo.name || !financingContactInfo.email || !financingContactInfo.phone) {
      setMsg('Please fill in all contact information');
      return;
    }

    try {
      // Prepare financing inquiry data
      const inquiryData = {
        name: financingContactInfo.name,
        email: financingContactInfo.email,
        phone: financingContactInfo.phone,
        awning_model: formData.model,
        awning_size: formData.size,
        total_amount: totalMax,
        required_deposit: requiredDeposit,
        financing_amount: financingAmount,
        source: 'awning_quote_form',
        referrer: document.referrer,
        user_agent: navigator.userAgent
      };

      // Submit to Supabase
      const { data, error } = await supabase
        .from('financing_inquiries')
        .insert([inquiryData])
        .select();

      if (error) {
        throw error;
      }

      console.log('Awning financing inquiry submitted:', data);

      // Store submitted data for success screen
      setSubmittedFinancingData({
        ...inquiryData,
        submissionId: data[0]?.id
      });

      // Close financing modal and show success screen
      setFinancingModalOpen(false);
      setFinancingSuccessOpen(true);
      setFinancingContactInfo({ name: '', email: '', phone: '' });

      // Google Analytics tracking
      if (typeof (window as any).gtag !== 'undefined') {
        (window as any).gtag('event', 'awning_financing_inquiry_submitted', {
          event_category: 'financing',
          event_label: `awning_${formData.size}`,
          value: Math.round(financingAmount),
          custom_parameters: {
            awning_size: formData.size,
            total_amount: totalMax,
            financing_amount: financingAmount,
            required_deposit: requiredDeposit
          }
        });
      }
    } catch (error) {
      console.error('Error submitting awning financing inquiry:', error);
      setMsg('Error submitting request. Please try again.');
    }
  };

  const getDisplayColor = () => {
    if (formData.color === 'other') {
      return customColor || 'Other Color';
    }
    return AWNING_COLORS.find(c => c.id === formData.color)?.name || '—';
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.model;
      case 2: return formData.size;
      case 3: return formData.color && (formData.color !== 'other' || customColor.trim());
      case 4: return formData.location;
      case 5: return true; // Add-ons are optional
      case 6: return true; // Quote summary
      case 7: return formData.name && formData.email && formData.phone &&
        formData.streetAddress && formData.city && formData.province &&
        formData.postalCode && EMAIL.test(formData.email) &&
        PHONE.test(formData.phone) && CA_POSTAL.test(formData.postalCode);
      default: return false;
    }
  };

  const nextStep = async () => {
    if (!canProceed() || step >= 8) return;

    const currentStep = step;
    const nextStepNumber = step + 1;

    // If moving from step 7 (contact info) to step 8 (final quote), save the data
    if (currentStep === 7 && nextStepNumber === 8) {
      console.log('Triggering database save for awning contact form submission');
      try {
        // Prepare quote submission data
        const quoteData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          street_address: formData.streetAddress,
          city: formData.city,
          province: formData.province,
          postal_code: formData.postalCode,
          pergola_model: formData.model || 'awning', // Using pergola_model field for awning model
          pergola_size: formData.size,
          pergola_color: formData.color === 'other' ? `Other: ${customColor}` : formData.color,
          pergola_location: formData.location,
          selected_addons: formData.addOns,
          base_price: basePrice,
          addons_total: addOnsTotal,
          install_cost: installCost + wallTypeSurcharge,
          delivery_fee: deliveryFee,
          subtotal: subtotal,
          hst: hst,
          total_with_hst: totalWithHST,
          total_min: totalMax,
          total_max: totalMax,
          action: 'contact-form-submit',
          source: 'awning_quote_form',
          referrer: document.referrer,
          user_agent: navigator.userAgent
        };

        console.log('Saving awning quote data on contact form submit:', quoteData);

        // Submit to Supabase
        const { data, error } = await supabase
          .from('quote_submissions')
          .insert([quoteData])
          .select();

        console.log('Awning contact form submission response:', { data, error });

        if (error) {
          console.error('Error saving awning contact form data:', error);
          // Don't block progression, but log the error
        } else {
          console.log('Awning contact form data saved successfully:', data);

          // Google Analytics tracking
          if (typeof (window as any).gtag !== 'undefined') {
            (window as any).gtag('event', 'contact_info_submitted', {
              event_category: 'lead_generation',
              event_label: `awning_${formData.size}`,
              value: Math.round(totalWithHST),
              custom_parameters: {
                awning_size: formData.size,
                awning_color: formData.color,
                wall_type: formData.location,
                total_amount: Math.round(totalWithHST)
              }
            });

            // Google Ads conversion tracking
            (window as any).gtag('event', 'conversion', {
              'send_to': 'AW-17868365802/pBSECPKF4OobEOq_pshC',
              'value': 500.0,
              'currency': 'CAD'
            });
          }
        }
      } catch (error) {
        console.error('Error in awning contact form submission:', error);
        // Don't block progression, but log the error
      }
    }

    setStep(nextStepNumber);
    setMsg('');
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      setMsg('');
    }
  };

  // Show loading state while pricing data is being fetched
  if (pricingLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white min-h-screen pb-24">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading awning options...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if pricing data failed to load
  if (pricingError) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white min-h-screen pb-24">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Unable to Load Awning Options</h2>
            <p className="text-red-600 mb-4">We're having trouble loading our current awning options. Please try refreshing the page.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white min-h-screen pb-24">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Get Your Instant Awning Quote</h1>
        <p className="text-gray-600">Get pricing in under 1 minute • No obligation • Professional installation included</p>
        <div className="text-xs text-gray-600 mt-2">★★★★★ 4.9/5 from Ontario homeowners • Motorized • Weather Resistant • Professional Installation</div>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Step {step} of 7</span>
          <span className="text-sm text-gray-500">{Math.round((step / 7) * 100)}% Complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${(step / 7) * 100}%` }}></div>
        </div>
      </div>

      {msg && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800">{msg}</p>
        </div>
      )}

      {/* Form Steps */}
      <div className="mb-8">
        {step === 1 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Choose Your Awning Model</h2>
            <p className="text-lg text-gray-600">Select the awning type that best fits your needs</p>

            {modelsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading awning models...</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6">
                {modelCards.map((model) => (
                  <div
                    key={model.id}
                    className={`relative p-6 border-2 rounded-lg cursor-pointer transition-all ${formData.model === model.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }`}
                    onClick={() => setFormData({ ...formData, model: model.id, size: '', addOns: [] })}
                  >
                    {model.badge && (
                      <div className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                        {model.badge}
                      </div>
                    )}
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{model.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{model.description}</p>
                      <div className="text-center mb-4">
                        <p className="text-lg font-semibold text-blue-600">
                          {currency(model.price)}
                        </p>
                        <p className="text-sm text-gray-600">
                          + {currency(model.installCost)} installation
                        </p>
                      </div>
                      <div className="text-left space-y-2">
                        <div className="text-sm font-medium text-gray-700">Best for:</div>
                        <p className="text-sm text-gray-600">{model.bestFor}</p>
                        <div className="text-sm font-medium text-gray-700 mt-3">Key features:</div>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {model.bullets.slice(0, 3).map((bullet, index) => (
                            <li key={index} className="flex items-start">
                              <Check size={14} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              {bullet}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Choose Your Awning Size</h2>
            <p className="text-lg text-gray-600">Select the perfect size for your space</p>

            {!formData.model ? (
              <div className="col-span-full text-center py-8 text-gray-500">
                Please select an awning model first
              </div>
            ) : currentModelPricing.length === 0 ? (
              <div className="col-span-full text-center py-8 text-gray-500">
                Loading sizes for {modelCards.find(m => m.id === formData.model)?.name}...
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentModelPricing.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`p-4 border-2 rounded-lg text-left transition-all ${formData.size === option.size_display_ft
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }`}
                    onClick={() => setFormData(prev => ({ ...prev, size: option.size_display_ft }))}
                  >
                    <div className="font-semibold text-lg">{option.size_display_ft}</div>
                    <div className="text-sm text-gray-600 mb-2">{option.size_display_m}</div>
                    <div className="text-blue-600 font-bold">{currency(option.retail_price)}</div>
                    <div className="text-sm text-gray-600">+ {currency(option.install_cost)} installation</div>
                    <div className="text-xs text-gray-500">or {currency(option.financing_monthly)}/month</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Choose Your Color</h2>
            <p className="text-lg text-gray-600">Select the color that matches your style</p>

            <div className="grid md:grid-cols-2 gap-4">
              {AWNING_COLORS.map((color) => (
                <button
                  key={color.id}
                  type="button"
                  className={`p-4 border-2 rounded-lg text-left transition-all flex items-center gap-4 ${formData.color === color.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, color: color.id }));
                    if (color.id !== 'other') {
                      setCustomColor('');
                    }
                  }}
                >
                  <div
                    className="w-12 h-12 rounded-full border-2"
                    style={{
                      background: color.color,
                      border: color.id === 'white' ? '2px solid #D1D5DB' : '2px solid #6B7280'
                    }}
                  ></div>
                  <div>
                    <div className="font-semibold">{color.name}</div>
                    <div className="text-sm text-gray-600">
                      {color.id === 'other' ? 'Specify your preferred color' : 'Weather-resistant fabric'}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {formData.color === 'other' && (
              <div className="mt-4">
                <label htmlFor="customColor" className="block text-sm font-medium text-gray-700 mb-2">
                  Specify Your Preferred Color
                </label>
                <input
                  type="text"
                  id="customColor"
                  value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)}
                  placeholder="e.g., Forest Green with White Stripes, Burgundy, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  We'll confirm color availability and provide samples if needed.
                </p>
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Wall Type</h2>
            <p className="text-lg text-gray-600">What type of wall will the awning be mounted to?</p>

            <div className="grid md:grid-cols-2 gap-4">
              {AWNING_WALL_TYPES.map((wallType) => (
                <button
                  key={wallType.id}
                  type="button"
                  className={`p-4 border-2 rounded-lg text-left transition-all ${formData.location === wallType.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => setFormData(prev => ({ ...prev, location: wallType.id }))}
                >
                  <div className="flex justify-between items-center">
                    <div className="font-semibold">{wallType.name}</div>
                    {wallType.extraCost > 0 && (
                      <div className="text-amber-600 font-bold">+{currency(wallType.extraCost)}</div>
                    )}
                  </div>
                  {wallType.extraCost > 0 && (
                    <div className="text-sm text-amber-600 mt-1">Additional installation complexity</div>
                  )}
                </button>
              ))}
            </div>

            <div className="text-center text-gray-600">
              <p>💡 Wall type affects mounting hardware and installation time</p>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Add-On Features</h2>
            <p className="text-lg text-gray-600">Enhance your awning with optional features</p>

            <div className="space-y-4">
              {compatibleAddons.map((addon) => (
                <div
                  key={addon.id}
                  className={`p-4 border-2 rounded-lg transition-all cursor-pointer ${formData.addOns.includes(addon.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      addOns: prev.addOns.includes(addon.id)
                        ? prev.addOns.filter(id => id !== addon.id)
                        : [...prev.addOns, addon.id]
                    }));
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-semibold">{addon.name}</div>
                      <div className="text-sm text-gray-600">{addon.description}</div>
                    </div>
                    <div className="text-blue-600 font-bold ml-4">+{currency(addon.price)}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center text-gray-600">
              <p>All add-ons are optional. You can always add them later.</p>
            </div>
          </div>
        )}

        {step === 6 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Your Awning Quote</h2>

            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6 rounded-lg">
              <p className="text-lg mb-2">Your Total Investment</p>
              <p className="text-4xl font-bold">{currency(totalMax)}</p>
              <p className="text-sm opacity-90 mt-1">All taxes and professional installation included</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Your Selection</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Size:</span>
                    <span className="font-medium">{formData.size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Color:</span>
                    <span className="font-medium">{getDisplayColor()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Wall Type:</span>
                    <span className="font-medium">{AWNING_WALL_TYPES.find(w => w.id === formData.location)?.name}</span>
                  </div>
                  {formData.addOns.length > 0 && (
                    <div className="pt-2 border-t">
                      <div className="font-medium mb-1">Add-ons:</div>
                      {formData.addOns.map(addonId => {
                        const addon = compatibleAddons.find(a => a.id === addonId);
                        return addon ? (
                          <div key={addonId} className="flex justify-between text-xs">
                            <span>{addon.name}</span>
                            <span>{currency(addon.price)}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Price Breakdown</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{modelCards.find(m => m.id === formData.model)?.name || 'Awning'} ({formData.size}):</span>
                    <span>{currency(basePrice)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Professional Installation:</span>
                    <span>{currency(installCost)}</span>
                  </div>
                  {wallTypeSurcharge > 0 && (
                    <div className="flex justify-between">
                      <span>Wall Type Surcharge ({wallType?.name}):</span>
                      <span>{currency(wallTypeSurcharge)}</span>
                    </div>
                  )}
                  {addOnsTotal > 0 && (
                    <div className="flex justify-between">
                      <span>Add-ons:</span>
                      <span>{currency(addOnsTotal)}</span>
                    </div>
                  )}
                  {deliveryFee > 0 && (
                    <div className="flex justify-between">
                      <span>Delivery (Outside GTA):</span>
                      <span>{currency(deliveryFee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-2">
                    <span>Subtotal:</span>
                    <span>{currency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>HST (13%):</span>
                    <span>{currency(hst)}</span>
                  </div>
                  <div className="flex justify-between font-bold border-t pt-2">
                    <span>Total:</span>
                    <span>{currency(totalMax)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <button
                type="button"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                onClick={() => setStep(7)}
              >
                <ChevronRight size={20} /> View Final Quote
              </button>
              <button
                type="button"
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-4 px-6 rounded-lg transition-colors"
                onClick={() => setShowExpensiveOptions(true)}
              >
                This is Too Expensive for Me
              </button>
            </div>
          </div>
        )}

        {step === 7 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Almost There!</h2>
            <p className="text-lg text-gray-600">Just a few details so we can send your quote and schedule your consultation.</p>

            <div className="text-xs text-gray-600">★★★★★ 4.9/5 from Ontario homeowners • "Professional installation." • "Perfect shade solution."</div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="name">Name *</label>
                <input
                  id="name"
                  type="text"
                  required
                  autoComplete="name"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="phone">Phone *</label>
                <input
                  id="phone"
                  type="tel"
                  required
                  inputMode="tel"
                  autoComplete="tel"
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formData.phone && !PHONE.test(formData.phone) ? 'border-red-400' : 'border-gray-300'}`}
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="email">Email *</label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                inputMode="email"
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formData.email && !EMAIL.test(formData.email) ? 'border-red-400' : 'border-gray-300'}`}
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Installation Address</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="streetAddress">Street Address *</label>
                <input
                  id="streetAddress"
                  type="text"
                  required
                  autoComplete="address-line1"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="123 Main Street"
                  value={formData.streetAddress}
                  onChange={(e) => setFormData(prev => ({ ...prev, streetAddress: e.target.value }))}
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="city">City *</label>
                  <input
                    id="city"
                    type="text"
                    required
                    autoComplete="address-level2"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Toronto"
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="province">Province *</label>
                  <select
                    id="province"
                    required
                    autoComplete="address-level1"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.province}
                    onChange={(e) => setFormData(prev => ({ ...prev, province: e.target.value }))}
                  >
                    <option value="">Select Province</option>
                    <option value="AB">Alberta</option>
                    <option value="BC">British Columbia</option>
                    <option value="MB">Manitoba</option>
                    <option value="NB">New Brunswick</option>
                    <option value="NL">Newfoundland and Labrador</option>
                    <option value="NT">Northwest Territories</option>
                    <option value="NS">Nova Scotia</option>
                    <option value="NU">Nunavut</option>
                    <option value="ON">Ontario</option>
                    <option value="PE">Prince Edward Island</option>
                    <option value="QC">Quebec</option>
                    <option value="SK">Saskatchewan</option>
                    <option value="YT">Yukon</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="postalCode">Postal Code *</label>
                  <input
                    id="postalCode"
                    type="text"
                    required
                    autoComplete="postal-code"
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formData.postalCode && !CA_POSTAL.test(formData.postalCode) ? 'border-red-400' : 'border-gray-300'}`}
                    placeholder="M5V 3A8"
                    value={formData.postalCode}
                    onChange={(e) => setFormData(prev => ({ ...prev, postalCode: normalizePostal(e.target.value) }))}
                  />
                </div>
              </div>

              <p className="text-sm text-gray-600">This helps us estimate delivery and installation logistics.</p>



            </div>
          </div>
        )}

        {step === 8 && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Awning Quote</h2>

              <div className="grid md:grid-cols-2 gap-4 text-left">
                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold mb-2">Summary</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li className="flex justify-between items-center py-1">
                      <span><span className="text-gray-500">Model:</span> {modelCards.find(m => m.id === formData.model)?.name || '—'}</span>
                      <button type="button" className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors" onClick={() => setStep(1)}>
                        Change
                      </button>
                    </li>
                    <li className="flex justify-between items-center py-1">
                      <span><span className="text-gray-500">Size:</span> {formData.size || '—'}</span>
                      <button type="button" className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors" onClick={() => setStep(2)}>
                        Change
                      </button>
                    </li>
                    <li className="flex justify-between items-center py-1">
                      <span><span className="text-gray-500">Color:</span> {getDisplayColor()}</span>
                      <button type="button" className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors" onClick={() => setStep(3)}>
                        Change
                      </button>
                    </li>
                    <li className="flex justify-between items-center py-1">
                      <span><span className="text-gray-500">Wall Type:</span> {AWNING_WALL_TYPES.find(w => w.id === formData.location)?.name || '—'}</span>
                      <button type="button" className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors" onClick={() => setStep(4)}>
                        Change
                      </button>
                    </li>
                    {formData.addOns.length > 0 && (
                      <li className="flex justify-between items-center py-1">
                        <span><span className="text-gray-500">Add-ons:</span> {formData.addOns.length} selected</span>
                        <button type="button" className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors" onClick={() => setStep(5)}>
                          Change
                        </button>
                      </li>
                    )}
                  </ul>
                </div>

                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold mb-2">Pricing</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>{modelCards.find(m => m.id === formData.model)?.name || 'Awning'} ({formData.size}): <span className="font-semibold">{currency(basePrice)}</span></li>
                    <li>Professional Installation: <span className="font-semibold">{currency(installCost)}</span></li>
                    {wallTypeSurcharge > 0 && <li>Wall Type Surcharge ({wallType?.name}): <span className="font-semibold">{currency(wallTypeSurcharge)}</span></li>}
                    {addOnsTotal > 0 && <li>Add-ons: <span className="font-semibold">{currency(addOnsTotal)}</span></li>}
                    {deliveryFee > 0 && <li>Delivery: <span className="font-semibold">{currency(deliveryFee)}</span></li>}
                    <li className="border-t pt-2">Subtotal: <span className="font-semibold">{currency(subtotal)}</span></li>
                    <li>HST (13%): <span className="font-semibold">{currency(hst)}</span></li>
                    <li className="border-t pt-2 font-semibold">Total: <span className="font-semibold">{currency(totalWithHST)}</span></li>
                  </ul>
                </div>
              </div>

              <div className="mt-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6 rounded-lg">
                <p className="text-lg mb-2">Your Total Investment</p>
                <p className="text-4xl font-bold" aria-live="polite">{currency(totalWithHST)}</p>
                <p className="text-sm opacity-90 mt-1">All taxes and professional installation included</p>
              </div>
            </div>

            {/* Direct Contact Option */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm">
              <p className="mb-2 text-gray-700">Prefer to speak with someone directly?</p>
              <p className="text-gray-600">
                Call us now: <a href="tel:+19056745983" className="text-accent hover:text-green-700 font-semibold">(905) 674-5983</a> |
                Toll Free: <a href="tel:+18554744653" className="text-accent hover:text-green-700 font-semibold">(855) 474-4653</a>
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex gap-4 mb-6">
                <button
                  type="button"
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                  onClick={() => setStep(6)}
                >
                  <ChevronLeft size={20} /> Back to Contact Info
                </button>
                <button
                  type="button"
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                  onClick={() => setStep(5)}
                >
                  ← Back to Summary
                </button>
              </div>

              <button
                type="button"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                disabled={emailSending}
                onClick={async () => {
                  // Check if we have contact information
                  if (!formData.name || !formData.email || !formData.phone) {
                    setMsg('Please provide your contact information first.');
                    setStep(6); // Go to contact form
                    return;
                  }

                  setEmailSending(true);
                  try {
                    // Note: Quote data is already saved when contact info was submitted
                    // No need to save again here - just send the email and track analytics

                    // Google Analytics tracking
                    if (typeof (window as any).gtag !== 'undefined') {
                      (window as any).gtag('event', 'awning_quote_email_requested', {
                        event_category: 'lead_generation',
                        event_label: `awning_${formData.size}`,
                        value: Math.round(totalWithHST),
                        custom_parameters: {
                          awning_size: formData.size,
                          total_amount: totalWithHST
                        }
                      });
                    }

                    setEmailSent(true);
                    setMsg('✅ Quote submitted! We will email your detailed awning quote within 24 hours. Check your inbox (and spam folder).');
                  } catch (error) {
                    console.error('Error submitting awning quote:', error);
                    setMsg('❌ We had trouble submitting your quote. Please try again or contact us directly.');
                  } finally {
                    setEmailSending(false);
                  }
                }}
              >
                <Mail size={20} />
                {emailSending ? 'Sending...' : emailSent ? '✅ Quote Request Sent!' : 'Send Me This Quote by Email'}
              </button>

              <button
                type="button"
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                onClick={async () => {
                  // Check if we have contact information
                  if (!formData.name || !formData.email || !formData.phone) {
                    setMsg('Please provide your contact information first to book a consultation.');
                    setStep(6); // Go to contact form
                    return;
                  }

                  try {
                    // Prepare consultation booking data
                    const consultationData = {
                      name: formData.name,
                      email: formData.email,
                      phone: formData.phone,
                      street_address: formData.streetAddress,
                      city: formData.city,
                      province: formData.province,
                      postal_code: formData.postalCode,
                      pergola_model: formData.model || 'awning', // Using pergola_model field for awning model
                      pergola_size: formData.size, // Using pergola_size field for awning size
                      pergola_color: formData.color === 'other' ? `Other: ${customColor}` : formData.color,
                      pergola_location: formData.location,
                      selected_addons: formData.addOns,
                      base_price: basePrice,
                      addons_total: addOnsTotal,
                      install_cost: installCost + wallTypeSurcharge,
                      delivery_fee: deliveryFee,
                      subtotal: subtotal,
                      hst: hst,
                      total_with_hst: totalWithHST,
                      total_min: totalMax,
                      total_max: totalMax,
                      action: 'book-consultation',
                      source: 'awning_quote_form',
                      referrer: document.referrer,
                      user_agent: navigator.userAgent
                    };

                    // Submit to Supabase
                    const { data, error } = await supabase
                      .from('quote_submissions')
                      .insert([consultationData])
                      .select();

                    if (error) {
                      throw error;
                    }

                    console.log('Awning consultation booking saved:', data);

                    // Google Analytics tracking
                    if (typeof (window as any).gtag !== 'undefined') {
                      (window as any).gtag('event', 'awning_consultation_booking_requested', {
                        event_category: 'lead_generation',
                        event_label: `awning_${formData.size}`,
                        value: Math.round(totalWithHST),
                        custom_parameters: {
                          awning_size: formData.size,
                          total_amount: totalWithHST,
                          submission_id: data[0]?.id
                        }
                      });
                    }

                    setMsg('✅ Your information has been saved! Opening Calendly to book your consultation...');

                    // Open Calendly after a short delay to show the success message
                    setTimeout(() => {
                      window.open('https://calendly.com/evieoutdoors', '_blank');
                    }, 1000);

                  } catch (error) {
                    console.error('Error saving awning consultation request:', error);
                    setMsg('❌ We had trouble saving your information. Please try again or contact us directly.');
                  }
                }}
              >
                <Calendar size={20} /> Book a Consultation / Site Visit
              </button>


            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      {step <= 7 && (
        <div className="flex justify-between">
          <button
            type="button"
            onClick={prevStep}
            disabled={step === 1}
            className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft size={20} /> Previous
          </button>

          {step < 7 && (
            <button
              type="button"
              onClick={nextStep}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
            >
              Next <ChevronRight size={20} />
            </button>
          )}

          {step === 7 && (
            <div className="flex gap-4">
              <button
                type="button"
                onClick={nextStep}
                disabled={!canProceed()}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-700 transition-colors"
              >
                Continue to Quote <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Contact Submit Success Modal */}
      {contactSubmitSuccessOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 text-center">
              {/* Success Icon */}
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>

              {/* Success Message */}
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Information Submitted!</h3>
              <p className="text-gray-600 mb-6">
                Thank you, {formData.name}! Your contact information has been saved. You can now view your final awning quote and choose how you'd like to proceed.
              </p>

              {/* Summary */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
                <h4 className="font-semibold text-gray-900 mb-3">Your Awning Quote Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Awning Size:</span>
                    <span className="font-medium">{formData.size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Color:</span>
                    <span className="font-medium">{getDisplayColor()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Location:</span>
                    <span className="font-medium">{formData.city}, {formData.province}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Investment:</span>
                    <span className="font-medium text-blue-600">{currency(totalMax)}</span>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6 text-left">
                <h4 className="font-semibold text-blue-900 mb-2">Next Steps</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• View your detailed final quote</li>
                  <li>• Email the quote to yourself for review</li>
                  <li>• Book a consultation or site visit</li>
                  <li>• Explore financing options if needed</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setContactSubmitSuccessOpen(false);
                  }}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setContactSubmitSuccessOpen(false);
                    setStep(7); // Go to final quote
                  }}
                  className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  View Final Quote
                </button>
              </div>

              {/* Contact Info */}
              <div className="mt-4 text-xs text-gray-500">
                Questions? Call us at <span className="font-medium">(905) 674-5983</span> or <span className="font-medium">(855) 474-4653</span> | Email: <span className="font-medium">hello@evieoutdoors.com</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expensive Options Modal */}
      {showExpensiveOptions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Let's Find a Better Fit</h3>
            <p className="text-gray-600 mb-6">We understand budget is important. Here are some options:</p>

            <div className="space-y-3">
              <button
                type="button"
                className="w-full text-left p-3 hover:bg-gray-50 rounded border transition-colors"
                onClick={() => {
                  setShowExpensiveOptions(false);
                  setStep(1); // Go back to size selection
                  setMsg('Consider a smaller awning size to reduce costs.');
                }}
              >
                💰 <strong>Choose a smaller size</strong> → Save $500-$1500
              </button>

              <button
                type="button"
                className="w-full text-left p-3 hover:bg-gray-50 rounded border transition-colors"
                onClick={() => {
                  setShowExpensiveOptions(false);
                  setStep(4); // Go back to add-ons
                  setMsg('Remove add-ons to reduce the total cost.');
                }}
              >
                🔧 <strong>Remove add-ons</strong> → Save up to $1200
              </button>

              <button
                type="button"
                className="w-full text-left p-3 hover:bg-gray-50 rounded border transition-colors"
                onClick={() => {
                  setShowExpensiveOptions(false);
                  setFinancingModalOpen(true);
                }}
              >
                💳 <strong>Explore financing</strong> → As low as {currency(currentPricing?.financing_monthly || 100)}/month
              </button>
            </div>

            <button
              type="button"
              className="w-full mt-4 py-2 text-gray-600 hover:text-gray-800"
              onClick={() => setShowExpensiveOptions(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Financing Modal */}
      {financingModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Awning Financing Options</h3>
                <button
                  onClick={() => setFinancingModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Financing Summary */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-blue-900 mb-2">Your Awning Investment</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-blue-800">Awning Size:</span>
                    <span className="font-medium text-blue-900">{formData.size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-800">Total Investment:</span>
                    <span className="font-medium text-blue-900">{currency(totalMax)}</span>
                  </div>
                  <div className="flex justify-between border-t border-blue-200 pt-2 mt-2">
                    <span className="text-blue-800">Required Deposit:</span>
                    <span className="font-medium text-blue-900">{currency(requiredDeposit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-800">Amount to Finance:</span>
                    <span className="font-bold text-blue-900">{currency(financingAmount)}</span>
                  </div>
                </div>
              </div>

              <h4 className="font-semibold text-gray-900 mb-2">Choose Your Payment Plan</h4>
              <div className="text-sm text-gray-600 mb-4">Monthly payments for financed amount ({currency(financingAmount)})</div>
              <div className="grid gap-4">
                {financingOptions.map((option) => (
                  <div key={option.term} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-semibold text-gray-900">{option.label}</div>
                        <div className="text-sm text-gray-600">{option.description}</div>
                        {option.rate === 0 && (
                          <div className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full inline-block mt-1">
                            Special Offer
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-600">{currency(option.payment)}</div>
                        <div className="text-sm text-gray-500">per month</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Get Pre-Approved Today</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Full Name"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={financingContactInfo.name}
                    onChange={(e) => setFinancingContactInfo(prev => ({ ...prev, name: e.target.value }))}
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={financingContactInfo.email}
                    onChange={(e) => setFinancingContactInfo(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-4"
                  value={financingContactInfo.phone}
                  onChange={(e) => setFinancingContactInfo(prev => ({ ...prev, phone: e.target.value }))}
                />

                <div className="text-xs text-gray-600 mb-4 mt-4">
                  * Subject to credit approval (O.A.C.). Financing provided by third-party lenders.
                  A financing specialist will contact you within 24 hours to discuss options and complete your application.
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setFinancingModalOpen(false)}
                    className="flex-1 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleFinancingSubmit}
                    className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Get Pre-Approved
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Financing Success Modal */}
      {financingSuccessOpen && submittedFinancingData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 text-center">
              {/* Success Icon */}
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>

              {/* Success Message */}
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Financing Request Submitted!</h3>
              <p className="text-gray-600 mb-6">
                Thank you, {submittedFinancingData?.name}! Your awning financing request has been submitted successfully.
              </p>

              {/* Summary */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
                <h4 className="font-semibold text-gray-900 mb-3">Your Financing Request</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Awning Size:</span>
                    <span className="font-medium">{submittedFinancingData?.awning_size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Investment:</span>
                    <span className="font-medium">{submittedFinancingData?.total_amount ? currency(submittedFinancingData.total_amount) : '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Required Deposit:</span>
                    <span className="font-medium">{submittedFinancingData?.required_deposit ? currency(submittedFinancingData.required_deposit) : '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Financing Amount:</span>
                    <span className="font-medium text-blue-600">{submittedFinancingData?.financing_amount ? currency(submittedFinancingData.financing_amount) : '—'}</span>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6 text-left">
                <h4 className="font-semibold text-blue-900 mb-2">What Happens Next?</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• A financing specialist will contact you within 24 hours</li>
                  <li>• They'll discuss your options and help with the application</li>
                  <li>• Pre-approval typically takes 1-2 business days</li>
                  <li>• Once approved, we'll schedule your awning installation</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setFinancingSuccessOpen(false);
                    setSubmittedFinancingData(null);
                  }}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back to Quote
                </button>
                <button
                  onClick={() => {
                    // Navigate to home page
                    window.location.href = '/';
                  }}
                  className="flex-1 py-3 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Back to Home Page
                </button>
              </div>

              {/* Contact Info */}
              <div className="mt-4 text-xs text-gray-500">
                Questions? Call us at <span className="font-medium">(905) 674-5983</span> or <span className="font-medium">(855) 474-4653</span> | Email: <span className="font-medium">hello@evieoutdoors.com</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Message */}
      {msg && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          {msg}
        </div>
      )}
    </div>
  );
};
